import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, ArrowRight, ChevronRight, Clock, Users, TrendingDown,
  Puzzle, DollarSign, CheckCircle2, Shield, Sparkles, BookOpen,
  FileText, Mic, Target, Award, BarChart3, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const IVY_STATS = [
  { school: 'Harvard', c2019: '5.3%', c2023: '4.5%', c2025: '3.4%', c2029: 'NYP' },
  { school: 'Yale', c2019: '6.5%', c2023: '5.9%', c2025: '4.6%', c2029: '4.6%' },
  { school: 'Princeton', c2019: '7.0%', c2023: '5.8%', c2025: '4.0%', c2029: 'NYP' },
  { school: 'Columbia', c2019: '6.1%', c2023: '5.1%', c2025: '3.7%', c2029: '4.3%' },
  { school: 'Brown', c2019: '8.6%', c2023: '6.6%', c2025: '5.4%', c2029: '5.7%' },
  { school: 'Dartmouth', c2019: '10.3%', c2023: '7.9%', c2025: '6.2%', c2029: '6.0%' },
  { school: 'Penn', c2019: '9.9%', c2023: '7.4%', c2025: '5.7%', c2029: '4.9%' },
  { school: 'Cornell', c2019: '14.9%', c2023: '10.6%', c2025: 'NYP', c2029: 'NYP' },
];

const WHAT_WE_DO = [
  { icon: BookOpen, title: 'Rigorous Coursework Planning', desc: 'Map out the most challenging schedule your student can handle — AP, IB, honors, dual enrollment — tailored to their strengths and target schools.' },
  { icon: FileText, title: 'Testing Strategy', desc: 'Plan a personalized SAT/ACT and AP exam strategy. Know which tests to take, when to take them, and how to prepare effectively.' },
  { icon: Sparkles, title: 'Activity Development', desc: 'Help students build a distinctive extracurricular profile that tells a compelling story — not a laundry list of clubs, but a narrative of impact.' },
  { icon: Target, title: 'Essay Coaching', desc: 'Brainstorm, draft, and refine outstanding essays tailored to each college. Our AI-powered tools plus human expertise ensure every word counts.' },
  { icon: Users, title: 'Recommendation Strategy', desc: 'Guide students on choosing recommenders and providing them with specific anecdotes and material to write powerful letters.' },
  { icon: Mic, title: 'Interview Preparation', desc: 'Practice with real questions, get AI-scored feedback, and build the confidence to make a lasting impression in every interview.' },
  { icon: Shield, title: 'Application Management', desc: 'Track every deadline, every document, every requirement across all schools. Our portal ensures nothing falls through the cracks.' },
  { icon: Award, title: 'Stress Reduction', desc: 'End the dinner-table arguments. We take the guesswork and conflict out of the process so families can focus on what matters.' },
];

export default function WhyHirePage() {
  const [showAllStats, setShowAllStats] = useState(false);

  return (
    <div className="min-h-screen bg-white pt-16" data-testid="why-hire-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-5 text-xs">The Case for Professional Guidance</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why You Should Invest<br />in a College Counselor
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            With admission rates at elite colleges plummeting below 5%, the difference between acceptance and rejection often comes down to strategy. Just as you'd hire a coach for athletics or a tutor for academics, professional admissions guidance is an investment in your child's future.
          </p>
        </div>
      </section>

      {/* Problem: HS Counselors */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="bg-red-50 text-red-600 border-red-200 text-xs mb-3">The Problem</Badge>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              High School Counselors Are Overwhelmed
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              The average public high school counselor is responsible for 470 students. With scheduling, mental health crises, and administrative duties, college admissions guidance often gets the smallest slice of their time. Even at elite prep schools, counselors may play favorites or discourage applications to prevent internal competition.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your child deserves more than a rushed 38-minute conversation about the most important decision of their young life.
            </p>
          </div>
          <div className="space-y-4">
            <Card className="border-red-200/60 bg-red-50/30">
              <CardContent className="p-5 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-red-500" />
                  <span className="text-3xl font-bold text-red-600">38 min</span>
                </div>
                <p className="text-xs text-muted-foreground">Average time a student spends with their high school counselor</p>
                <p className="text-[9px] text-muted-foreground/60 mt-1">Source: U.S. Department of Education</p>
              </CardContent>
            </Card>
            <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02]">
              <CardContent className="p-5 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-[#0F392B]" />
                  <span className="text-3xl font-bold text-[#0F392B]">470:1</span>
                </div>
                <p className="text-xs text-muted-foreground">Average public school counselor-to-student ratio</p>
                <p className="text-[9px] text-muted-foreground/60 mt-1">Source: Education Week</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Admission Rates */}
      <section className="bg-[#F9F9F7] py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-xs mb-3">
              <TrendingDown className="h-3 w-3 mr-1" /> The Reality
            </Badge>
            <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admission Rates Are at Historic Lows
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
              The competition has never been fiercer. Every year, acceptance rates at the nation's top universities continue to fall.
            </p>
          </div>

          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="ivy-stats-table">
            <CardContent className="p-0">
              <div className="px-5 py-3 bg-[#0F392B] text-white flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs font-semibold">Ivy League Acceptance Rates Over Time</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#E2E2E2] bg-[#F9F9F7]">
                      <th className="text-left py-2.5 px-4 font-semibold">School</th>
                      <th className="text-center py-2.5 px-3 font-semibold">Class of '19</th>
                      <th className="text-center py-2.5 px-3 font-semibold">Class of '23</th>
                      <th className="text-center py-2.5 px-3 font-semibold">Class of '25</th>
                      <th className="text-center py-2.5 px-3 font-semibold text-[#D4AF37]">Class of '29</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllStats ? IVY_STATS : IVY_STATS.slice(0, 4)).map((s, i) => (
                      <tr key={i} className="border-b border-[#F2F2F0] hover:bg-[#F9F9F7] transition-colors">
                        <td className="py-2.5 px-4 font-medium">{s.school}</td>
                        <td className="text-center py-2.5 px-3 text-muted-foreground">{s.c2019}</td>
                        <td className="text-center py-2.5 px-3 text-muted-foreground">{s.c2023}</td>
                        <td className="text-center py-2.5 px-3 text-muted-foreground">{s.c2025}</td>
                        <td className="text-center py-2.5 px-3 font-semibold text-red-600">{s.c2029}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!showAllStats && (
                <div className="px-4 py-2.5 text-center border-t border-[#F2F2F0]">
                  <button onClick={() => setShowAllStats(true)} className="text-[10px] text-[#0F392B] font-medium hover:underline">
                    Show all 8 Ivy League schools
                  </button>
                </div>
              )}
              <div className="px-4 py-2 bg-[#F9F9F7] border-t border-[#E2E2E2]">
                <p className="text-[9px] text-muted-foreground">NYP = not yet published. Source: Ivy League admissions data, institutional reports.</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <Card className="border-[#E2E2E2]">
              <CardContent className="p-5 text-center">
                <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-2" />
                <span className="text-2xl font-bold text-foreground">5 full classes</span>
                <p className="text-xs text-muted-foreground mt-1">Worth of students with perfect grades and scores are denied at schools like Harvard every year</p>
              </CardContent>
            </Card>
            <Card className="border-[#E2E2E2]">
              <CardContent className="p-5 text-center">
                <TrendingDown className="h-5 w-5 text-red-500 mx-auto mb-2" />
                <span className="text-2xl font-bold text-foreground">&lt;5%</span>
                <p className="text-xs text-muted-foreground mt-1">Admission rates at many elite schools have fallen below 5% — and they're still dropping</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Investment Pays Off */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-8">
          <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-xs mb-3">The Return</Badge>
          <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Top Colleges Cost More, But Pay Off
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
            Elite education is an investment that compounds over a lifetime. The data speaks for itself.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]">
            <CardContent className="p-5 text-center">
              <DollarSign className="h-5 w-5 text-[#D4AF37] mx-auto mb-2" />
              <span className="text-xl font-bold">$90K+</span>
              <p className="text-xs text-muted-foreground mt-1">Median earnings 10 years after graduation from Ivy League schools</p>
              <p className="text-[9px] text-muted-foreground/60 mt-1">Source: CNBC</p>
            </CardContent>
          </Card>
          <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02]">
            <CardContent className="p-5 text-center">
              <Award className="h-5 w-5 text-[#0F392B] mx-auto mb-2" />
              <span className="text-xl font-bold">8 of 9</span>
              <p className="text-xs text-muted-foreground mt-1">U.S. Supreme Court Justices attended Ivy League schools</p>
              <p className="text-[9px] text-muted-foreground/60 mt-1">Source: CNBC</p>
            </CardContent>
          </Card>
          <Card className="border-[#E2E2E2]">
            <CardContent className="p-5 text-center">
              <Users className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
              <span className="text-xl font-bold">26%+</span>
              <p className="text-xs text-muted-foreground mt-1">Of high-achieving students use private college counselors (likely higher today)</p>
              <p className="text-[9px] text-muted-foreground/60 mt-1">Source: Lipman Hearne Study</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-[#0F392B] text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Ivy League Consulting Does for You
            </h2>
            <p className="text-sm text-white/60 mt-2 max-w-lg mx-auto">
              Good college counselors don't just fill out forms. They build a strategic case for your child's admission to their dream school.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHAT_WE_DO.map((item, i) => (
              <div key={i} className="flex gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-4 w-4 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-1">{item.title}</h3>
                  <p className="text-xs text-white/60 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-xs mb-3">Our Philosophy</Badge>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Ivy League Consulting Approach
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              We combine the personal touch of dedicated admissions counselors with the power of AI-driven tools. Our portal gives students a complete Common App-style workspace, while our expertise ensures every piece of their application tells a cohesive, compelling story.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              We don't sugarcoat. If a student's profile isn't competitive for their dream school, we'll have that honest conversation — and then we'll build a strategy for where they <em>can</em> win. Because the students who get in aren't always the ones with the highest scores. They're the ones who present the most <em>distinctive</em> case.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              When elite universities deny five full classes of students with perfect grades and scores, grades and scores are simply the table stakes. What gets you in is the story you tell — and that's what we help you craft.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { label: 'You + A Current Ivy League Student', desc: 'A peer who recently navigated the same process' },
              { label: 'An Ivy League Professional', desc: 'Industry expertise to shape your career narrative' },
              { label: 'Your Admissions Team', desc: 'Strategists who know what top schools actually look for' },
              { label: 'AI-Powered Tools', desc: 'Essay coach, activity optimizer, interview prep — all in one portal' },
            ].map((item, i) => (
              <Card key={i} className="border-[#E2E2E2]">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#0F392B] flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold">{item.label}</h4>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Puzzle Pieces */}
      <section className="bg-[#F9F9F7] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Puzzle className="h-8 w-8 text-[#0F392B] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Every Piece of the Puzzle Matters
          </h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-8">
            SAT/ACT scores, AP tests, rigor of coursework, grades, college essays, letters of recommendation, interviews, activities… There are dozens of components. A college consultant takes the mystery out of the process and helps you assemble every piece strategically.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['Coursework', 'Testing', 'Activities', 'Essays', 'Recommendations', 'Interviews', 'Deadlines', 'Financial Aid'].map((piece, i) => (
              <Card key={i} className="border-[#E2E2E2]">
                <CardContent className="p-3 flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#0F392B]" />
                  <span className="text-xs font-medium">{piece}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Build Your Strategy?
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Don't leave your child's college admissions to chance. Our $99/year portal gives you the tools, templates, and AI-powered guidance that elite consulting clients pay thousands for.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up">
              <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 text-sm font-semibold" data-testid="main-cta-btn">
                Start for $99/year <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/career-portal">
              <Button variant="outline" className="h-12 px-8 text-sm">
                Explore Free Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Statistics sourced from U.S. Department of Education, institutional reports, and industry studies</p>
        </div>
      </footer>
    </div>
  );
}
